import { Box, Button, Card, CardContent, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, Grid, Icon, IconButton, InputLabel, styled, Switch, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from "@mui/material";
import { DataGridPro } from "@mui/x-data-grid-pro/DataGridPro";
import { useQuery } from "@tanstack/react-query";
import FallbackSpinner from "../../materio/@core/components/spinner";
import { client } from "../../utils/axios-client";
import CustomChip from 'src/materio/@core/components/mui/chip'
import PageHeader from "../../materio/@core/components/page-header";
import { useEffect, useState } from "react";
import { cloneDeep } from "lodash";
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import CloseIcon from '@mui/icons-material/Close';
import { ApiResponseType } from "../../types";
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import EditIcon from '@mui/icons-material/Edit';
import ApplicationStore from "../../stores/ApplicationStore";
import ColorDropdown from "../common2/Dropdowns/ColorDropdown";
import IconDropdown from "../common2/Dropdowns/IconDropdown";
import CustomIcon from "../common/CustomIcon";
import OutcomeList from "../../v2/features/Outcomes/OutcomList/OutcomeList";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    padding: 2,
    verticalAlign: 'top'
}));

interface CellType {
    row: ActivityTypeListItem
}

const defaultColumns = [
    {
        flex: 0.65,
        field: 'name',
        minWidth: 240,
        headerName: 'Name',
        renderCell: ({ row }: CellType) => <Typography>{row.name}</Typography>
    }
]

export type ActivityTypeListItem = {
    id: string,
    isActive: boolean,
    name: string,
    color: string,
    hasOutcomes: boolean,
    icon: string,
    numberOfOutcomes: number,
    [key: string]: any
}

const ActivityTypeList = () => {
    const [data, setData] = useState<ActivityTypeListItem[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [editMode, setEditMode] = useState(false);
    const [open, setOpen] = useState<boolean>(false)
    const [selectedActivityType, setSelectedActivityType] = useState(null as null | ActivityTypeListItem);
    const [outcomeListOpen, setOutcomeListOpen] = useState(false);

    //useEffect(() => {
    //    if (data) {
    //        setSelectedActivityType(data[0]);
    //        setOutcomeListOpen(true);
    //    }
    //}, [data])

    useEffect(() => { 
        loadRecords()
    }, [])

    const loadRecords = async () => {
        //setIsLoading(true);
        const response = await client.get(`/api/activity-types`);
        setData(response.data);
        setIsLoading(false);
    }


    if (isLoading) return <FallbackSpinner />;

    if (!data) return <Box>Error fetching activity types</Box>;

    const columns = [
        ...defaultColumns,
        {
            flex: 0.15,
            minWidth: 215,
            field: 'isActice',
            headerName: 'Status',

            renderCell: ({ row }: CellType) => <Typography>{row.isActive ? <CustomChip label="Active" color='success' sx={{ minWidth: 100 }} /> : <CustomChip label="Inactive" color="error" sx={{ minWidth: 100 }} />}</Typography>
        }
    ];

    const onOpenOutcomes = (row: ActivityTypeListItem) => {
        setSelectedActivityType(row);
        setOutcomeListOpen(true);
    }

    const onOutcomeListClose = async () => {
        await loadRecords();
        setSelectedActivityType(null);
        setOutcomeListOpen(false);
    }

    const onUpdateInt = async (index: number, prop: string, value: string) => {
        const temp = cloneDeep(data);
        temp[index][prop] = parseInt(value);
        setData(temp);
    }

    const onUpdateString = async (prop: string, value: string) => {
        if (!selectedActivityType) return;

        const temp = cloneDeep(selectedActivityType);
        temp[prop] = value;
        setSelectedActivityType(temp);
    }

    const onUpdateBoolean = async (index: number, prop: string, value: boolean) => {
        const temp = cloneDeep(data);
        temp[index][prop] = value;
        setData(temp);
    }

    const onSaveDialog = async () => {
        const response = await client.post<ApiResponseType>('/api/activity-types', { items: [selectedActivityType] });
        if (response.data.succeeded) {
            loadRecords();

            ApplicationStore.update(s => {
                s.snackbarMessage = "Activity Type Saved Successfully";
                s.snackbarOpen = true;
            })

            handleClose();
        } else {
            alert('error updating activity type');
        }
    }

    const handleClickOpen = (item: ActivityTypeListItem) => {
        setSelectedActivityType(item);
        setOpen(true);
    }

    const handleClose = async () => {
        await loadRecords();
        setOpen(false)
        setSelectedActivityType(null);                
    }

    const renderCards = () =>
        data.map((item, index: number) => (
            <Grid item xs={12} sm={6} lg={3} key={index} sx={{ padding: 3 }}>
                <Card style={{ cursor: 'pointer' }}>
                    <CardContent>
                        <Box>
                            <Typography variant='h6' textAlign='center'>{item.name}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 5, mb: 5, gap: 5, cursor: 'pointer' }}>
                            <CustomIcon icon={item.icon} sx={{ color: item.color }} />
                            {item.hasOutcomes && <Typography
                                onClick={() => onOpenOutcomes(item)}
                                variant='body2'
                                sx={{ color: 'primary.main', cursor: 'pointer' }}>
                                {item.numberOfOutcomes} Outcomes
                            </Typography>}
                            {!item.hasOutcomes && <Typography
                                onClick={() => onOpenOutcomes(item)}
                                variant='body2'
                                sx={{ color: 'primary.main', cursor: 'pointer' }}>
                                &nbsp;
                            </Typography>}
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography
                                variant='body2'
                                sx={{ color: 'primary.main', cursor: 'pointer' }}
                                onClick={() => {
                                    handleClickOpen(item)
                                }}
                            >
                                Edit
                            </Typography>
                        </Box>
                    </CardContent>
                </Card>
            </Grid>
        ))

    return <Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '80vh', maxHeight: '80vh', rowGap: 8 }}>
            <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                <PageHeader
                    title={<Typography variant='h5'>Outcome Activity Types</Typography>}
                />
            </Box>

            <Box>
                <Grid container rowGap={3}>
                    {renderCards()}
                </Grid>
            </Box>
        </Box>
        {selectedActivityType && <OutcomeList open={outcomeListOpen} onClose={onOutcomeListClose} activityType={selectedActivityType} />}

        {selectedActivityType && <Dialog fullWidth maxWidth='sm' scroll='body' onClose={handleClose} open={open}>
            <DialogTitle sx={{ textAlign: 'center' }}>
                <Typography variant='h4' component='span'>
                    Edit Activity Type
                </Typography>
                <Typography variant='body2'>Set activity type icon and color</Typography>
            </DialogTitle>
            <DialogContent sx={{ p: { xs: 6, sm: 12 } }}>
                <Box sx={{ my: 4 }}>
                    <Grid container rowGap={3}>
                        <Grid item xs={3}></Grid>
                        <Grid item xs={6}>
                            <FormControl fullWidth>
                                <InputLabel>Color</InputLabel>
                                <ColorDropdown label="Color" value={selectedActivityType.color} onChange={(newValue) => { onUpdateString('color', newValue) }} />
                            </FormControl>
                        </Grid>
                        <Grid item xs={3}></Grid>
                        <Grid item xs={3}></Grid>
                        <Grid item xs={6}>
                            <FormControl fullWidth>
                                <InputLabel>Icon</InputLabel>
                                <IconDropdown label="Icon" value={selectedActivityType.icon} onChange={(newValue) => { onUpdateString('icon', newValue) }} />
                            </FormControl>
                        </Grid>
                        <Grid item xs={3}></Grid>
                    </Grid>
                </Box>
            </DialogContent>
            <DialogActions sx={{ pt: 0, display: 'flex', justifyContent: 'center' }}>
                <Box>
                    <Button size='small' type='button' variant='contained' sx={{ mr: 10 }} onClick={onSaveDialog}>
                        Save
                    </Button>
                    <Button size='small' color='secondary' variant='outlined' onClick={handleClose}>
                        Cancel
                    </Button>
                </Box>
            </DialogActions>
        </Dialog>}
    </Box>
}

ActivityTypeList.guestGuard = false;
ActivityTypeList.authGuard = true;


export default ActivityTypeList;